/**
 * Pure helpers describing what a Firebase user is allowed to do.
 *
 * The Firebase Auth uid stays the only identity in the app. These helpers only
 * describe the provider attached to that uid; they never derive a party role.
 * Party role is still decided by event.hostId in utils/identity.js.
 */

import { isPrivateLanOrigin, LAN_GOOGLE_AUTH_MESSAGE } from './authOrigin';

export const GOOGLE_PROVIDER_ID = 'google.com';

export const ACCOUNT_TYPE = {
  anonymous: 'anonymous',
  google: 'google',
};

export function hasGoogleProvider(user) {
  if (!user) return false;
  const providers = Array.isArray(user.providerData) ? user.providerData : [];
  return providers.some((entry) => entry?.providerId === GOOGLE_PROVIDER_ID);
}

export function isAnonymousUser(user) {
  if (!user) return false;
  if (hasGoogleProvider(user)) return false;
  return user.isAnonymous === true;
}

export function deriveAccountType(user) {
  if (!user) return null;
  if (hasGoogleProvider(user)) return ACCOUNT_TYPE.google;
  if (user.isAnonymous === true) return ACCOUNT_TYPE.anonymous;
  return ACCOUNT_TYPE.anonymous;
}

/**
 * Hosting requires a durable account so the host can recover a party later.
 * Guests intentionally never need this.
 */
export function canHostParty(user) {
  return hasGoogleProvider(user);
}

/** An anonymous session should be linked rather than replaced by a new sign-in. */
export function shouldLinkAnonymousUser(user) {
  return isAnonymousUser(user);
}

export const LINK_OUTCOME = {
  linked: 'linked',
  alreadyGoogle: 'already-google',
  signedInInstead: 'signed-in-instead',
  redirectStarted: 'redirect-started',
  cancelled: 'cancelled',
  failed: 'failed',
};

export const GENERIC_DISPLAY_NAMES = new Set(['guest', 'unknown']);

export function isGenericDisplayName(name) {
  if (typeof name !== 'string') return true;
  const trimmed = name.trim();
  if (!trimmed) return true;
  return GENERIC_DISPLAY_NAMES.has(trimmed.toLowerCase());
}

/** Prefer the top-level Auth name, then a Google providerData entry. */
export function authDisplayName(user) {
  const top = typeof user?.displayName === 'string' ? user.displayName.trim() : '';
  if (top) return top;
  const providers = Array.isArray(user?.providerData) ? user.providerData : [];
  for (const entry of providers) {
    const name = typeof entry?.displayName === 'string' ? entry.displayName.trim() : '';
    if (name) return name;
  }
  return '';
}

/**
 * Maps a Firebase Auth error code to a recovery strategy.
 *
 * `credential-already-in-use` / `email-already-in-use` mean the Google account
 * already owns a different uid. Linking cannot merge two uids, so the only safe
 * recovery is signing in to the existing Google identity; the anonymous uid is
 * left intact rather than being destroyed.
 */
export function classifyAuthError(error, extras = {}) {
  const code = typeof error === 'string' ? error : error?.code || '';
  const origin = extras.origin
    ?? (typeof window !== 'undefined' ? window.location?.origin : '');

  switch (code) {
    case 'auth/credential-already-in-use':
    case 'auth/email-already-in-use':
    case 'auth/account-exists-with-different-credential':
      return {
        kind: 'credential-in-use',
        recoverable: true,
        strategy: 'sign-in-to-existing-account',
        message: 'That Google account is already set up on Rhythmé. Signing you in to it instead.',
      };
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return {
        kind: 'cancelled',
        recoverable: true,
        strategy: 'ignore',
        message: 'Google sign-in was cancelled.',
      };
    case 'auth/popup-blocked':
    case 'auth/operation-not-supported-in-this-environment':
      return {
        kind: 'popup-blocked',
        recoverable: true,
        strategy: 'retry-with-redirect',
        message: 'Your browser blocked the Google sign-in window. Continuing in this window…',
      };
    case 'auth/requires-recent-login':
      return {
        kind: 'requires-recent-login',
        recoverable: true,
        strategy: 'reauthenticate',
        message: 'Please sign in with Google again to continue.',
      };
    case 'auth/operation-not-allowed':
      return {
        kind: 'provider-not-configured',
        recoverable: false,
        strategy: 'contact-admin',
        message: 'Google sign-in is not enabled for this Rhythmé deployment yet.',
      };
    case 'auth/unauthorized-domain':
      if (isPrivateLanOrigin(origin)) {
        return {
          kind: 'unauthorized-lan-origin',
          recoverable: false,
          strategy: 'use-supported-origin',
          message: LAN_GOOGLE_AUTH_MESSAGE,
        };
      }
      return {
        kind: 'provider-not-configured',
        recoverable: false,
        strategy: 'contact-admin',
        message: 'Google sign-in is not enabled for this Rhythmé deployment yet.',
      };
    default:
      return {
        kind: 'unknown',
        recoverable: false,
        strategy: 'report',
        message: 'Google sign-in failed. Please try again.',
      };
  }
}

/**
 * Longest participant name Firestore rules accept. Kept here so the client
 * clamps instead of producing a write the rules would reject.
 */
export const DISPLAY_NAME_MAX_LENGTH = 64;

/**
 * Firebase Auth's Google provider always hosts avatars on
 * lh3–lh6.googleusercontent.com. Anything else is dropped so a self-join write
 * cannot smuggle an attacker-controlled URL past the matching Firestore rule.
 */
const GOOGLE_AVATAR_PATTERN = /^https:\/\/lh[3-6]\.googleusercontent\.com\//;

export function sanitizeParticipantPhotoURL(url) {
  if (typeof url !== 'string' || !url) return null;
  return GOOGLE_AVATAR_PATTERN.test(url) ? url : null;
}

/**
 * Display name resolution for participant records and profile documents.
 * Falls back through the Google profile, a typed name, then a neutral label.
 */
export function resolveDisplayName(user, typedName = '', extras = {}) {
  const extraNames = Array.isArray(extras.fallbackNames)
    ? extras.fallbackNames
    : extras.fallbackName
      ? [extras.fallbackName]
      : [];

  const candidates = [
    typeof typedName === 'string' ? typedName.trim() : '',
    authDisplayName(user),
    ...extraNames.map((name) => (typeof name === 'string' ? name.trim() : '')),
  ].filter(Boolean);

  const preferred = candidates.find((name) => !isGenericDisplayName(name));
  if (preferred) return preferred.slice(0, DISPLAY_NAME_MAX_LENGTH);
  return 'Guest';
}
